// app/projects/SkillsNeedingAttention.jsx
import { useEffect, useRef, useState } from 'react';
import { HelpCircle } from '../../lib/icons';
import { cn } from '../../lib/cn';
import { SCORE_MARK, scoreMarkInk } from '../ds';
import { masteryColors } from './scoring';

const TOP_N_DEFAULT = 5;
const ROW_GRID = 'grid grid-cols-[28px_minmax(0,1.5fr)_minmax(0,1.4fr)_72px] items-center gap-3';

export default function SkillsNeedingAttention({ skills, selectedSkillId, onSelectSkill }) {
  const [showHelp, setShowHelp] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Dismiss the mastery tip on outside click or Escape — clicking the tip
  // itself already closes it, but a click anywhere else used to leave it up.
  const helpRef = useRef(null);
  useEffect(() => {
    if (!showHelp) return;
    const onPointerDown = (e) => {
      if (helpRef.current && !helpRef.current.contains(e.target)) setShowHelp(false);
    };
    const onKey = (e) => {
      // preventDefault so a window-level Esc listener (e.g. the disc) can tell
      // this tip already consumed the key and not also back itself out.
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowHelp(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showHelp]);

  if (!skills || skills.length === 0) {
    return (
      <div className="ds p-5 text-sm text-muted-foreground">
        No skill developmental descriptors yet.
      </div>
    );
  }

  const sorted = [...skills].sort((a, b) => {
    const aGraded = a.n_graded > 0;
    const bGraded = b.n_graded > 0;
    if (aGraded !== bGraded) return aGraded ? -1 : 1;
    if (aGraded && bGraded) {
      if (a.mastery_pct !== b.mastery_pct) return a.mastery_pct - b.mastery_pct;
    }
    return a.specific_skill.localeCompare(b.specific_skill);
  });

  const visible = showAll ? sorted : sorted.slice(0, TOP_N_DEFAULT);
  const hasMore = sorted.length > TOP_N_DEFAULT;

  return (
    <div className="ds px-5 pb-4 pt-3.5">
      <div className="relative mb-2" ref={helpRef}>
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          aria-expanded={showHelp}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <HelpCircle className="size-3.5" />
          What does mastery mean?
        </button>
        {showHelp && (
          <div
            className="absolute z-10 mt-1.5 max-w-md space-y-2 rounded-xl border border-border bg-popover p-3.5 text-[12.5px] leading-relaxed text-muted-foreground shadow-lg [&_strong]:font-semibold [&_strong]:text-foreground"
            onClick={() => setShowHelp(false)}
          >
            <p>
              <strong>Mastery</strong> is the percentage of graded students who scored at level 4 or
              higher on the skill developmental descriptor.
            </p>
            <p>
              A score of <strong>1–2</strong> means re-teach; <strong>3</strong> is developing;{' '}
              <strong>4</strong> is proficient for the grade; <strong>5</strong> is clearly strong
              work beyond it.
            </p>
            <p>
              Each row is one of the {skills.length} skill developmental descriptors, ranked by
              lowest mastery. The
              skill map above shows where each one sits in the curriculum.
            </p>
            <p className="text-[11px] italic">Click anywhere to dismiss.</p>
          </div>
        )}
      </div>

      <div
        className={cn(
          ROW_GRID,
          'border-b border-border px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'
        )}
      >
        <div>#</div>
        <div>Skill developmental descriptor</div>
        <div>Score distribution (1–5)</div>
        <div className="text-right">Mastery</div>
      </div>

      <div className="mt-1 flex flex-col gap-1">
        {visible.map((s, i) => {
          const selected = s.specific_skill_id === selectedSkillId;
          const mc = masteryColors(s.mastery_pct);
          const masteryPct = s.mastery_pct != null ? Math.round(s.mastery_pct * 100) : null;

          return (
            <button
              key={s.specific_skill_id}
              type="button"
              onClick={() => onSelectSkill?.(selected ? null : s.specific_skill_id)}
              title="Click to filter the matrix below to this skill developmental descriptor"
              className={cn(
                ROW_GRID,
                'rounded-lg border px-2 py-2 text-left transition-colors',
                selected
                  ? 'border-primary bg-primary/5'
                  : 'border-transparent hover:border-border hover:bg-secondary/40'
              )}
            >
              <span className="text-xs font-bold text-muted-foreground">{i + 1}</span>
              <div className="min-w-0">
                {/* full text at rest — names wrap (2 lines) instead of needing
                    hover to be readable; the row grid absorbs the extra line */}
                <div className="line-clamp-2 text-[13px] font-semibold leading-snug text-foreground">
                  {s.specific_skill}
                </div>
                <div className="text-[10.5px] text-muted-foreground/80">
                  {s.n_graded} student{s.n_graded === 1 ? '' : 's'} graded
                </div>
              </div>

              {s.n_graded > 0 ? (
                <div className="flex h-4 gap-[2px]">
                  {[1, 2, 3, 4, 5].map((level) => {
                    const count = s.distribution[level] || 0;
                    if (count === 0) return null;
                    const pct = (count / s.n_graded) * 100;
                    return (
                      <div
                        key={level}
                        className="flex min-w-[2rem] items-center justify-center gap-1 rounded-[3px] text-[10px] font-bold"
                        style={{
                          flexGrow: pct,
                          flexBasis: 0,
                          background: SCORE_MARK[level],
                          color: scoreMarkInk(level),
                        }}
                        title={`Level ${level}: ${count} student${count === 1 ? '' : 's'} (${pct.toFixed(0)}%)`}
                      >
                        <span className="font-semibold opacity-80">L{level}</span>
                        {count}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex h-4 items-center justify-center rounded-[3px] bg-secondary text-[10px] italic text-muted-foreground">
                  Not yet graded
                </div>
              )}

              <div
                className="flex flex-col items-center rounded-md px-2 py-1 tabular-nums"
                style={{ background: mc.bg, color: mc.fg }}
              >
                <span className="text-[13px] font-bold leading-none">
                  {masteryPct != null ? `${masteryPct}%` : '—'}
                </span>
                <span className="text-[8px] font-semibold uppercase tracking-wide opacity-80">
                  Mastery
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 text-xs font-semibold text-primary hover:underline"
        >
          {showAll
            ? `Show top ${TOP_N_DEFAULT}`
            : `Show all ${sorted.length} skill developmental descriptors`}
        </button>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-2.5 text-[11px] text-muted-foreground">
        <span className="font-semibold uppercase tracking-wide">Score levels</span>
        {[1, 2, 3, 4, 5].map((level) => (
          <span key={level} className="inline-flex items-center gap-1" title={`Level ${level}`}>
            <span className="size-3 rounded-sm" style={{ background: SCORE_MARK[level] }} />
            <span>{level}</span>
          </span>
        ))}
        <span className="text-muted-foreground/80">
          Mastery = % of graded students at level 4 or higher
        </span>
      </div>
    </div>
  );
}
